import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { completeAppointmentPayout } from '../../services/appointmentApi';
import toast from 'react-hot-toast';
import { Send, Loader2 } from 'lucide-react';

const PayoutDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [payout, setPayout] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [sendingPayoutNotif, setSendingPayoutNotif] = useState(false);
  const [showProcessModal, setShowProcessModal] = useState(false);
  const [transactionId, setTransactionId] = useState('');
  const [transactionMode, setTransactionMode] = useState('bank_transfer');

  const handleSendPayoutNotification = async () => {
    const targetLeadId = payout?.lead?._id || payout?.lead;
    if (!targetLeadId) {
      toast.error('No lead associated with this payout');
      return;
    }
    setSendingPayoutNotif(true);
    try {
      const res = await api.post(`/leads/${targetLeadId}/notify-payout`);
      const msg = typeof res.data?.message === 'string' ? res.data.message : 'Payout email notification sent successfully!';
      toast.success(msg);
    } catch (error) {
      const err = typeof error.response?.data?.message === 'string' ? error.response.data.message : 'Failed to send payout notification';
      toast.error(err);
    } finally {
      setSendingPayoutNotif(false);
    }
  };

  useEffect(() => {
    fetchPayout();
  }, [id]);

  const fetchPayout = async () => {
    try {
      const response = await api.get(`/payouts/${id}`);
      setPayout(response.data.data);
    } catch (error) {
      toast.error('Failed to load payout');
      navigate('/payouts');
    } finally {
      setLoading(false);
    }
  };

  const handleProcess = async () => {
    if (!transactionId) {
      toast.error('Please enter transaction ID');
      return;
    }

    setUpdating(true);
    try {
      await api.post(`/payouts/${id}/process`, {
        transactionId,
        transactionMode
      });
      toast.success('Payout processing started!');
      setShowProcessModal(false);
      setTransactionId('');
      setTransactionMode('bank_transfer');
      fetchPayout();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to process payout');
    } finally {
      setUpdating(false);
    }
  };

  const handleComplete = async () => {
    if (!window.confirm('Are you sure you want to mark this payout as completed?')) {
      return;
    }

    setUpdating(true);
    try {
      if (payout.payoutType === 'appointment') {
        await completeAppointmentPayout(payout._id);
      } else {
        await api.post(`/payouts/${payout._id}/complete`);
      }
      toast.success('Payout completed successfully!');
      fetchPayout();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to complete payout');
    } finally {
      setUpdating(false);
    }
  };

  const handleFail = async () => {
    const reason = prompt('Please enter the reason for failure:');
    if (!reason) return;

    setUpdating(true);
    try {
      await api.post(`/payouts/${id}/fail`, { reason });
      toast.success('Payout marked as failed!');
      fetchPayout();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to mark payout as failed');
    } finally {
      setUpdating(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      processing: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!payout) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Payout not found</p>
        <Link to="/payouts" className="text-primary-600 hover:text-primary-700">
          Back to payouts
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fadeIn pb-20">
      {/* Header & Actions */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div className="flex items-center gap-6">
          <button
            onClick={() => navigate('/payouts')}
            className="w-12 h-12 bg-white border border-gray-100 rounded-2xl flex items-center justify-center text-gray-400 hover:text-blue-600 hover:border-blue-100 hover:bg-blue-50 transition-all shadow-sm group"
          >
            <span className="transform group-hover:-translate-x-1 transition-transform">←</span>
          </button>
          <div>
            <h1 className="text-3xl font-black text-[#143E78] tracking-tight">Settlement Detail</h1>
            <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mt-1">Transaction ID: <span className="text-blue-500 font-black">{payout.transactionId || 'Pending Allocation'}</span></p>
          </div>
        </div>
        
        <div className="flex flex-wrap items-center gap-3">
          {payout?.lead && (payout.lead.isManualPastEntry || payout.lead.source === 'past_surgery' || payout.lead.leadGeneratedByType === 'past_surgery' || payout.lead.razorpayDetails?.lastPaymentId === 'MANUAL_SETTLEMENT') && (
            <button
              onClick={handleSendPayoutNotification}
              disabled={sendingPayoutNotif}
              className="px-6 py-3 bg-amber-400 text-slate-950 text-[11px] font-black uppercase tracking-widest rounded-2xl shadow-xl hover:bg-amber-300 transition-all flex items-center gap-2 disabled:opacity-50 cursor-pointer"
            >
              {sendingPayoutNotif ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              Send Payout Email
            </button>
          )}
          {user?.role === 'admin' && payout.status === 'pending' && (
            <button
              onClick={() => setShowProcessModal(true)}
              className="px-8 py-3 bg-blue-600 text-white text-[11px] font-black uppercase tracking-widest rounded-2xl shadow-xl shadow-blue-200 hover:scale-105 active:scale-95 transition-all flex items-center gap-2"
            >
               Process Payout
            </button>
          )}
          {user?.role === 'admin' && payout.status === 'processing' && (
            <>
              <button
                onClick={handleFail}
                disabled={updating}
                className="px-6 py-3 border border-red-100 bg-red-50 text-red-600 text-[11px] font-black uppercase tracking-widest rounded-2xl hover:bg-red-100 transition-all disabled:opacity-50"
              >
                Mark Failed
              </button>
              <button
                onClick={handleComplete}
                disabled={updating}
                className="px-8 py-3 bg-emerald-600 text-white text-[11px] font-black uppercase tracking-widest rounded-2xl shadow-xl shadow-emerald-200 hover:scale-105 active:scale-95 transition-all disabled:opacity-50"
              >
                Complete Settlement
              </button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Left Column: Financial Data */}
        <div className="lg:col-span-8 space-y-8">
          
          {/* Main Status Header */}
          <div className="bg-gradient-to-br from-[#143E78] to-[#0A254A] rounded-[40px] p-8 sm:p-10 text-white relative overflow-hidden shadow-2xl shadow-blue-900/20">
             <div className="absolute top-0 right-0 p-12 opacity-10 pointer-events-none">
                <div className="w-40 h-40 bg-white rounded-full blur-3xl animate-pulse"></div>
             </div>
             
             <div className="relative z-10">
                <div className="flex flex-wrap items-center justify-between gap-6 mb-8">
                   <div className="flex items-center gap-4">
                      <div className="w-14 h-14 bg-white/10 backdrop-blur-xl rounded-2xl flex items-center justify-center border border-white/10">
                         <span className="text-2xl font-black italic">₹</span>
                      </div>
                      <div>
                         <p className="text-[10px] font-black uppercase tracking-[0.2em] text-blue-200/60 mb-1">Total Settlement</p>
                          <h2 className="text-4xl font-black tracking-tighter italic">₹{(payout.payoutType === 'appointment' ? (payout.doctor?.financials?.consultationFee || payout.amount || 0) : (payout.amount || 0)).toLocaleString('en-IN')}</h2>
                      </div>
                   </div>
                   <div className={`px-6 py-2 rounded-full text-[10px] font-black uppercase tracking-widest border backdrop-blur-xl ${
                      payout.status === 'completed' ? 'bg-emerald-500/20 border-emerald-500/30 text-emerald-300' : 
                      payout.status === 'processing' ? 'bg-blue-500/20 border-blue-500/30 text-blue-300' :
                      'bg-yellow-500/20 border-yellow-500/30 text-yellow-300'
                   }`}>
                      {payout.status}
                   </div>
                </div>
                
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-8 pt-8 border-t border-white/5">
                   <div>
                      <p className="text-[9px] font-black uppercase tracking-widest text-blue-200/40 mb-2">Payout Type</p>
                      <p className="text-sm font-black uppercase italic tracking-tight">{payout.payoutType?.replace(/_/g, ' ')}</p>
                   </div>
                   <div>
                      <p className="text-[9px] font-black uppercase tracking-widest text-blue-200/40 mb-2">Currency</p>
                      <p className="text-sm font-black italic">{payout.currency}</p>
                   </div>
                   <div className="col-span-2 sm:col-span-1">
                      <p className="text-[9px] font-black uppercase tracking-widest text-blue-200/40 mb-2">Date Created</p>
                      <p className="text-sm font-black italic">{new Date(payout.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
                   </div>
                </div>
             </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
             {/* Doctor Share Card */}
             {payout.doctorShare && (
               <div className="bg-white rounded-[32px] p-8 border border-gray-100 shadow-xl shadow-gray-200/30 group hover:border-blue-100 transition-all">
                 <div className="flex items-center gap-4 mb-6">
                   <div className="w-12 h-12 bg-blue-50 rounded-2xl flex items-center justify-center text-blue-600 transition-transform group-hover:scale-110">
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                   </div>
                   <h3 className="text-sm font-black text-[#143E78] uppercase tracking-widest">Doctor's Allocation</h3>
                 </div>
                 <div className="space-y-4">
                    <div className="flex justify-between items-end pb-4 border-b border-gray-50">
                       <span className="text-xs font-bold text-gray-400 uppercase tracking-tighter">Amount</span>
                        <span className="text-2xl font-black text-[#143E78] tracking-tighter">₹{(payout.payoutType === 'appointment' ? (payout.doctor?.financials?.consultationFee || payout.doctorShare?.amount || 0) : (payout.doctorShare?.amount || 0)).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center">
                       <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Revenue Share</span>
                       <span className="px-3 py-1 bg-blue-50 text-blue-600 text-[10px] font-black rounded-lg">{payout.doctorShare.percentage}%</span>
                    </div>
                 </div>
               </div>
             )}

             {/* Hospital Share Card */}
             {payout.hospitalShare && (
               <div className="bg-white rounded-[32px] p-8 border border-gray-100 shadow-xl shadow-gray-200/30 group hover:border-emerald-100 transition-all">
                 <div className="flex items-center gap-4 mb-6">
                   <div className="w-12 h-12 bg-emerald-50 rounded-2xl flex items-center justify-center text-emerald-600 transition-transform group-hover:scale-110">
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
                   </div>
                   <h3 className="text-sm font-black text-[#143E78] uppercase tracking-widest">Hospital's Allocation</h3>
                 </div>
                 <div className="space-y-4">
                    <div className="flex justify-between items-end pb-4 border-b border-gray-50">
                       <span className="text-xs font-bold text-gray-400 uppercase tracking-tighter">Amount</span>
                       <span className="text-2xl font-black text-[#143E78] tracking-tighter">₹{payout.hospitalShare.amount?.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center">
                       <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Revenue Share</span>
                       <span className="px-3 py-1 bg-emerald-50 text-emerald-600 text-[10px] font-black rounded-lg">{payout.hospitalShare.percentage}%</span>
                    </div>
                 </div>
               </div>
             )}
          </div>

          {/* Transaction Metadata */}
          {payout.transactionId && (
            <div className="bg-white rounded-[32px] p-8 border border-gray-100 shadow-xl shadow-gray-200/30">
               <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-8">Transaction Intelligence</h3>
               <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                  <div className="space-y-1">
                     <p className="text-[9px] font-black text-slate-300 uppercase tracking-widest">Bank Reference / ID</p>
                     <p className="text-sm font-black text-[#143E78] font-mono break-all bg-slate-50 p-4 rounded-2xl border border-slate-100">{payout.transactionId}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-6">
                     <div>
                        <p className="text-[9px] font-black text-slate-300 uppercase tracking-widest mb-1">Method</p>
                        <p className="text-sm font-black text-[#143E78] uppercase italic">{payout.transactionMode?.replace(/_/g, ' ')}</p>
                     </div>
                     <div>
                        <p className="text-[9px] font-black text-slate-300 uppercase tracking-widest mb-1">Time Captured</p>
                        <p className="text-sm font-black text-[#143E78] italic">
                           {payout.transactionDate ? new Date(payout.transactionDate).toLocaleTimeString() : 'N/A'}
                        </p>
                     </div>
                  </div>
               </div>
            </div>
          )}

          {payout.description && (
            <div className="bg-white rounded-[32px] p-8 border border-gray-100 shadow-xl shadow-gray-200/30">
              <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-4">Operations Note</h3>
              <p className="text-gray-600 font-medium leading-relaxed italic border-l-4 border-blue-100 pl-6">{payout.description}</p>
            </div>
          )}
        </div>

        {/* Right Column: Entity Relationships */}
        <div className="lg:col-span-4 space-y-8">
          
          {/* Patient Card */}
          <div className="bg-white rounded-[32px] p-8 border border-gray-100 shadow-xl shadow-gray-200/30 group">
            <h3 className="text-[9px] font-black text-gray-300 uppercase tracking-[0.2em] mb-6">Linked Patient</h3>
            <div className="flex items-center gap-4">
               <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center text-white text-lg font-black shadow-lg shadow-blue-100">
                  {payout.lead?.patientName?.charAt(0)}
               </div>
               <div>
                  <p className="text-base font-black text-[#143E78] uppercase italic leading-tight">{payout.lead?.patientName}</p>
                  <p className="text-[11px] font-bold text-slate-400 mt-1">{payout.lead?.patientPhone}</p>
               </div>
            </div>
            <Link 
              to={`/surgeries/${payout.surgery?._id || payout.surgery}`}
              className="mt-8 w-full py-4 bg-slate-50 border border-slate-100 rounded-2xl text-[10px] font-black uppercase tracking-widest text-slate-400 flex items-center justify-center gap-2 hover:bg-blue-600 hover:text-white hover:border-blue-600 transition-all group"
            >
               View Clinical Booking <span className="transform group-hover:translate-x-1 transition-transform">→</span>
            </Link>
          </div>

          {/* Practitioner Info */}
          <div className="bg-[#143E78] rounded-[32px] p-8 text-white shadow-2xl shadow-blue-900/20">
            <h3 className="text-[9px] font-black text-white/30 uppercase tracking-[0.2em] mb-6">Assigned Doctor</h3>
            <div className="flex flex-col gap-4">
               <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center border border-white/10">
                     <svg className="w-6 h-6 text-blue-200" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" /></svg>
                  </div>
                  <div>
                     <p className="text-sm font-black tracking-tight italic">Dr. {payout.doctor?.user?.firstName} {payout.doctor?.user?.lastName}</p>
                     <p className="text-[10px] font-bold text-blue-300/60 uppercase">{payout.doctor?.specialization}</p>
                  </div>
               </div>
            </div>
            
            <div className="mt-8 pt-6 border-t border-white/5">
               <h3 className="text-[9px] font-black text-white/30 uppercase tracking-[0.2em] mb-4">Facility Partner</h3>
               <p className="text-sm font-black italic">{payout.hospital?.hospitalName}</p>
               <p className="text-[10px] font-bold text-blue-300/60 mt-1">{payout.hospital?.address?.city}</p>
            </div>
          </div>

          {/* Audit Log (Simplified) */}
          <div className="bg-white rounded-[32px] p-8 border border-gray-100 shadow-xl shadow-gray-200/30">
            <h3 className="text-[9px] font-black text-gray-300 uppercase tracking-[0.2em] mb-6">Compliance Trail</h3>
            <div className="space-y-6">
               {payout.processedBy && (
                  <div className="relative pl-6 border-l-2 border-blue-50">
                     <div className="absolute -left-[5px] top-0 w-2 h-2 rounded-full bg-blue-500"></div>
                     <p className="text-[10px] font-black text-[#143E78] uppercase mb-1">Processed</p>
                     <p className="text-[9px] font-bold text-slate-400">{payout.processedBy?.firstName} • {new Date(payout.processedAt).toLocaleDateString()}</p>
                  </div>
               )}
               {payout.isVerified && (
                  <div className="relative pl-6 border-l-2 border-emerald-50">
                     <div className="absolute -left-[5px] top-0 w-2 h-2 rounded-full bg-emerald-500"></div>
                     <p className="text-[10px] font-black text-emerald-600 uppercase mb-1">Verified</p>
                     <p className="text-[9px] font-bold text-slate-400">{payout.verifiedBy?.firstName} • {new Date(payout.verifiedAt).toLocaleDateString()}</p>
                  </div>
               )}
               <div className="relative pl-6">
                  <div className="absolute -left-[5px] top-0 w-2 h-2 rounded-full bg-slate-200"></div>
                  <p className="text-[10px] font-black text-slate-400 uppercase mb-1">Initiated</p>
                  <p className="text-[9px] font-bold text-slate-400">{new Date(payout.createdAt).toLocaleDateString()}</p>
               </div>
            </div>
          </div>

        </div>
      </div>

      {/* Process Modal */}
      {showProcessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-lg font-semibold mb-4">Process Payout</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Transaction ID</label>
                <input
                  type="text"
                  value={transactionId}
                  onChange={(e) => setTransactionId(e.target.value)}
                  placeholder="Enter transaction ID"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Transaction Mode</label>
                <select
                  value={transactionMode}
                  onChange={(e) => setTransactionMode(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="upi">UPI</option>
                  <option value="cheque">Cheque</option>
                  <option value="cash">Cash</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end space-x-4 mt-6">
              <button
                onClick={() => setShowProcessModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleProcess}
                disabled={updating}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
              >
                {updating ? 'Processing...' : 'Process'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PayoutDetail;
